/**
 * AffiliateContext — hält den JWT + Profil des angemeldeten Shop-Partners.
 * Klein & fokussiert, keine große Feature-Suite: nur login, logout, aktueller
 * Partner. Der Rest (Statistiken, Profil-Update) läuft direkt aus den Pages.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'hs_affiliate_token';

const AffiliateContext = createContext(null);

export const AffiliateProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [affiliate, setAffiliate] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (tk = token) => {
    if (!tk) { setAffiliate(null); setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/api/shop/affiliates/me`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setAffiliate(data.affiliate || null);
    } catch (e) {
      // Token abgelaufen / ungültig
      localStorage.removeItem(STORAGE_KEY);
      setToken('');
      setAffiliate(null);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/shop/affiliates/login`, { email, password });
    localStorage.setItem(STORAGE_KEY, data.token);
    setToken(data.token);
    setAffiliate(data.affiliate);
    return data.affiliate;
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken('');
    setAffiliate(null);
  };

  return (
    <AffiliateContext.Provider value={{ token, affiliate, loading, login, logout, refresh, setAffiliate }}>
      {children}
    </AffiliateContext.Provider>
  );
};

export const useAffiliate = () => {
  const ctx = useContext(AffiliateContext);
  if (!ctx) throw new Error('useAffiliate must be used within AffiliateProvider');
  return ctx;
};
